// LiveWindow — shell autónomo para o monitor Live numa janela própria.
//
// É montado pelo App.js quando o hash é `#/live/<routeId>` (ver
// utils/liveWindow.js → openLiveWindow). Replica o mínimo do ambiente
// do Planner para o LiveRouteMonitor funcionar isolado:
//   • Redux store próprio (slice `vehicles` alimentada pelo WS)
//   • WebSocket próprio (connectWS) — recebe `vehicles:update` da sala
//     do cliente, exactamente como o Planner; não há polling extra ao
//     Traccar, logo várias janelas não pesam no servidor.
//   • AuthContext com o JWT, para os fetch autenticados do monitor.

import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Box, CircularProgress, Typography, Button } from '@material-ui/core';

import store from '../../store';
import theme from '../../theme';
import { AuthContext } from '../../App';
import { connectWS, disconnectWS } from '../../utils/websocket';
import { setVehiclesFromWS } from '../../store/slices/vehiclesSlice';
import { LIVE_TOKEN_KEY } from '../../utils/liveWindow';
import LiveRouteMonitor from './LiveRouteMonitor';

function parseRouteId() {
  const m = (window.location.hash || '').match(/#\/live\/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

// O monitor exige um AuthContext completo; só o token é usado neste modo.
const AUTH_SHAPE = {
  modules: [], clientSettings: {}, clientType: null,
  clientName: null, userEmail: null, userRole: null,
  updateClientSettings: () => {},
};

function Centered({ children }) {
  return (
    <Box style={{
      display: 'flex', flexDirection: 'column', gap: 14,
      alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0f172a', color: '#e2e8f0',
      fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif',
    }}>
      {children}
    </Box>
  );
}

export default function LiveWindow() {
  const routeId = parseRouteId();
  const [token, setToken] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | noauth

  // Resolver o token, validá-lo e ligar o WebSocket — tudo antes de
  // montar o LiveRouteMonitor. O WS tem de existir já quando o monitor
  // monta: os efeitos do filho correm antes dos do pai, logo o socket
  // tem de estar criado antes de o monitor chamar getSocket().
  useEffect(() => {
    let t = null;
    try {
      t = sessionStorage.getItem('planner_token');
      if (!t) {
        t = localStorage.getItem(LIVE_TOKEN_KEY);
        if (t) {
          sessionStorage.setItem('planner_token', t);
          localStorage.removeItem(LIVE_TOKEN_KEY);
        }
      }
    } catch { /* storage indisponível */ }

    if (!t) { setStatus('noauth'); return undefined; }

    let cancelled = false;
    fetch('/planner-api/auth/me', { headers: { Authorization: `Bearer ${t}` } })
      .then(r => { if (!r.ok) throw new Error('invalid'); return r.json(); })
      .then(() => {
        if (cancelled) return;
        // WebSocket próprio desta janela — alimenta a slice `vehicles`.
        // O backend já faz o polling do Traccar uma vez e difunde para a
        // sala do cliente; várias janelas só recebem o mesmo broadcast.
        connectWS(t, {
          onVehicles: (vehicles) => {
            if (Array.isArray(vehicles)) store.dispatch(setVehiclesFromWS(vehicles));
          },
        });
        setToken(t);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        try { sessionStorage.removeItem('planner_token'); } catch { /* ignore */ }
        setStatus('noauth');
      });

    return () => { cancelled = true; disconnectWS(); };
  }, []);

  useEffect(() => {
    document.title = routeId ? `Live · Rota #${routeId} — EcooGPS` : 'Live — EcooGPS';
  }, [routeId]);

  if (!routeId) {
    return (
      <Centered>
        <Typography style={{ fontWeight: 700 }}>Endereço inválido</Typography>
        <Typography style={{ fontSize: '0.85rem', opacity: 0.7 }}>
          Não foi indicada nenhuma rota. Abre o Live a partir do Planner.
        </Typography>
      </Centered>
    );
  }

  if (status === 'loading') {
    return <Centered><CircularProgress style={{ color: '#3b82f6' }} /></Centered>;
  }

  if (status === 'noauth') {
    return (
      <Centered>
        <Typography style={{ fontWeight: 700 }}>Sessão indisponível</Typography>
        <Typography style={{ fontSize: '0.85rem', opacity: 0.7, textAlign: 'center', maxWidth: 320 }}>
          Esta janela perdeu a sessão. Fecha-a e abre o Live novamente a
          partir do Planner.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => window.close()}
          style={{ color: '#e2e8f0', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }}
        >
          Fechar janela
        </Button>
      </Centered>
    );
  }

  return (
    <AuthContext.Provider value={{ token, ...AUTH_SHAPE }}>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LiveRouteMonitor routeId={routeId} onClose={() => window.close()} />
        </ThemeProvider>
      </Provider>
    </AuthContext.Provider>
  );
}
